import {
  HoldingsService,
  // types: requests
  HoldingsServiceOptions,
  GetAccountHoldingsOptions,
  GetHoldingsByTickerIdListOptions,
  GetMasterHoldingStatusesOptions,
  GetUpdateClientHoldingOptions,
  GetAssetManagersOptions,
  GetModelPortfoliosOptions,
  GetModelPortfolioDetailsOptions,
  CreateModelPortfolioOptions,
  GetHoldingReferenceOptions,
  DeleteModelPortfolioOptions,
  GetGlidePathFrameworkOptions,
  PostGlidePathFrameworkOptions,
  DeleteGlidePathOptions,
  GetSleeveOptions,
  PostSleeveOptions,
  DeleteSleeveOptions,
  // types: responses
  AccountHolding,
  HoldingsByTickerIdList,
  MasterHoldingStatus,
  HoldingByTicker,
  AssetManagerResponse,
  ModelPortfolioResponse,
  ModelPortfolioTickerResponse,
  HoldingReferenceResponse,
  GlidePathFrameworkResponse,
  SleeveResponse,
  GlidePathFrameworkTypeResponse,
  UpdateAssetAllocationForAccountIdOptions,
  UpdateAssetAllocationForGoalIdOptions,
} from '@digital-advice/ms-holdings-sdk';
import {apiKey, getLogger, resolveEndpointForServiceToken, interceptors, sourceInfo} from '@advisor/services/sdk';

const svcOptions = {hasServiceTokenEndpoint: false};

export const defaultOptions: HoldingsServiceOptions = {
  logger: getLogger(),
  apiKey,
  endpoint: resolveEndpointForServiceToken(svcOptions.hasServiceTokenEndpoint),
  interceptors,
  sourceInfo,
};

export default class HoldingsServices {
  static async sdkService(options: HoldingsServiceOptions = defaultOptions): Promise<HoldingsService> {
    const service = await new HoldingsService(options);

    return service;
  }

  /**
   * Gateway endpoint: getAccountHoldings
   * @param payload {GetAccountHoldingsOptions} {accountId}
   */
  static async getAccountHoldings(payload: GetAccountHoldingsOptions): Promise<AccountHolding> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getAccountHoldings(payload);
  }

  /**
   * Gateway endpoint: getHoldingsByTickerIdList
   * @param payload {GetAccountHoldingsOptions} {clientId, tickIdList}
   */
  static async getHoldingsByTickerIdList(payload: GetHoldingsByTickerIdListOptions): Promise<HoldingsByTickerIdList> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getHoldingsByTickerIdList(payload);
  }

  /**
   * Gateway endpoint: getHoldingsByClient
   * @param payload {GetAccountHoldingsOptions} {clientId, accountList, holdingType}
   */
  static async getHoldingsByClient(payload: GetHoldingsByTickerIdListOptions): Promise<HoldingsByTickerIdList> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getHoldingsByClient(payload);
  }

  /**
   * Gateway endpoint: getMasterHoldingStatuses
   * @param payload {GetMasterHoldingStatusesOptions} {type}
   */
  static async getMasterHoldingStatuses(payload: GetMasterHoldingStatusesOptions): Promise<MasterHoldingStatus[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getMasterHoldingStatuses(payload);
  }

  /**
   * Gateway endpoint: updateClientHolding
   * @param payload {GetUpdateClientHoldingOptions} {clientId, holdingSettingRequestList}
   */
  static async updateClientHolding(payload: GetUpdateClientHoldingOptions): Promise<HoldingByTicker[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateClientHolding(payload);
  }

  /**
   * Gateway endpoint: getAssetManagers
   * @param payload {GetAssetManagersOptions} {riaId, riaComponent}
   */
  static async getAssetManagers(payload: GetAssetManagersOptions): Promise<AssetManagerResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getAssetManagers(payload);
  }

  /**
   * Gateway endpoint: getModelPortfolios
   * @param payload {GetModelPortfoliosOptions} {riaId, assetManagerId, enterpriseId, riaComponent,
   *                searchTerm, limit, offset}
   */
  static async getModelPortfolios(payload: GetModelPortfoliosOptions): Promise<ModelPortfolioResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getModelPortfolios(payload);
  }

  /**
   * Gateway endpoint: getModelPortfolioData
   * @param payload {GetModelPortfolioDetailsOptions} {riaId, modelId, enterpriseId, riaComponent}
   */
  static async getModelPortfolioData(payload: GetModelPortfolioDetailsOptions): Promise<ModelPortfolioResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getModelPortfolioData(payload);
  }

  /**
   * Gateway endpoint: getPortfolioTickers
   * @param payload {GetModelPortfolioDetailsOptions} {riaId, modelId, enterpriseId, riaComponent}
   */
  static async getPortfolioTickers(payload: GetModelPortfolioDetailsOptions): Promise<ModelPortfolioTickerResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getPortfolioTickers(payload);
  }

  /**
   * Gateway endpoint: createModelPortfolioData
   * @param payload {CreateModelPortfolioOptions} {riaId, modelId, enterpriseId, assetManagerId,
   *                assetManagerName, description, modelName, modelPortfolioDetail[], riaComponent}
   */
  static async createModelPortfolioData(payload: CreateModelPortfolioOptions): Promise<ModelPortfolioResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.createModelPortfolioData(payload);
  }

  /**
   * Gateway endpoint: updateModelPortfolioData
   * @param payload {CreateModelPortfolioOptions} {riaId, modelId, enterpriseId, assetManagerId,
   *                assetManagerName, description, modelName, modelPortfolioDetail[], riaComponent}
   */
  static async updateModelPortfolioData(payload: CreateModelPortfolioOptions): Promise<ModelPortfolioResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateModelPortfolioData(payload);
  }

  /**
   * Gateway endpoint: getReferences
   * @param payload {GetHoldingReferenceOptions} {riaId, id, type}
   */
  static async getReferences(payload: GetHoldingReferenceOptions): Promise<HoldingReferenceResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getReferences(payload);
  }

  /**
   * Gateway endpoint: deleteModelPortfolio
   * @param payload {GetHoldingReferenceOptions} {riaId, modelId, enterpriseId, riaComponent}
   */
  static async deleteModelPortfolio(payload: DeleteModelPortfolioOptions): Promise<unknown> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.deleteModelPortfolio(payload);
  }

  /**
   * Gateway endpoint: getGlidePathFrameworks
   * @param payload {GetHoldingReferenceOptions} {riaId, frameworkId?, riaComponent}
   */
  static async getGlidePathFrameworks(payload: GetGlidePathFrameworkOptions): Promise<GlidePathFrameworkResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getGlidePathFrameworks(payload);
  }

  /**
   * Gateway endpoint: getGlidePathFramework
   * @param payload {GetHoldingReferenceOptions} {riaId, frameworkId?, riaComponent}
   */
  static async getGlidePathFramework(payload: GetGlidePathFrameworkOptions): Promise<GlidePathFrameworkResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getGlidePathFramework(payload);
  }

  /**
   * Gateway endpoint: getGlidePathFrameworkTypes
   * @param payload {GetHoldingReferenceOptions} {riaId, riaComponent}
   */
  static async getGlidePathFrameworkTypes(
    payload: GetGlidePathFrameworkOptions
  ): Promise<GlidePathFrameworkTypeResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getGlidePathFrameworkTypes(payload);
  }

  /**
   * Gateway endpoint: createGlidePathFrameworks
   * @param payload {PostGlidePathFrameworkOptions} {riaId, frameworkId?, riaComponent}
   */
  static async createGlidePathFrameworks(payload: PostGlidePathFrameworkOptions): Promise<GlidePathFrameworkResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.createGlidePathFrameworks(payload);
  }

  /**
   * Gateway endpoint: updateGlidePathFrameworks
   * @param payload {PostGlidePathFrameworkOptions} {riaId, frameworkId?, riaComponent}
   */
  static async updateGlidePathFrameworks(payload: PostGlidePathFrameworkOptions): Promise<GlidePathFrameworkResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateGlidePathFrameworks(payload);
  }

  /**
   * Gateway endpoint: deleteGlidePathFramework
   * @param payload {DeleteGlidePathOptions} {riaId, frameworkId?, riaComponent}
   */
  static async deleteGlidePathFramework(payload: DeleteGlidePathOptions): Promise<unknown> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.deleteGlidePathFramework(payload);
  }

  /**
   * Gateway endpoint: getSleeves
   * @param payload {GetSleeveOptions} {riaId, riaComponent}
   */
  static async getSleeves(payload: GetSleeveOptions): Promise<SleeveResponse[]> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getSleeves(payload);
  }

  /**
   * Gateway endpoint: getSleeveById
   * @param payload {GetSleeveOptions} {riaId, sleeveId, riaComponent}
   */
  static async getSleeveById(payload: GetSleeveOptions): Promise<SleeveResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getSleeveById(payload);
  }

  /**
   * Gateway endpoint: getSleeveTickers
   * @param payload {GetSleeveOptions} {riaId, sleeveId, riaComponent}
   */
  static async getSleeveTickers(payload: GetSleeveOptions): Promise<SleeveResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.getSleeveTickers(payload);
  }

  /**
   * Gateway endpoint: createSleeve
   * @param payload {PostSleeveOptions} {riaId, riaComponent}
   */
  static async createSleeve(payload: PostSleeveOptions): Promise<SleeveResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.createSleeve(payload);
  }

  /**
   * Gateway endpoint: updateSleeve
   * @param payload {PostSleeveOptions} {riaId, sleeveId, riaComponent}
   */
  static async updateSleeve(payload: PostSleeveOptions): Promise<SleeveResponse> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateSleeve(payload);
  }

  /**
   * Gateway endpoint: deleteSleeve
   * @param payload {DeleteSleeveOptions} {riaId, sleeveId, riaComponent}
   */
  static async deleteSleeve(payload: DeleteSleeveOptions): Promise<unknown> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.deleteSleeve(payload);
  }

  /**
   * Gateway endpoint: updateAssetAllocationForAccountId
   * @param payload {UpdateAssetAllocationForAccountIdOptions} {accountId, async}
   */
  static async updateAssetAllocationForAccountId(payload: UpdateAssetAllocationForAccountIdOptions): Promise<unknown> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateAssetAllocationForAccountId(payload);
  }

  /**
   * Gateway endpoint: updateAssetAllocationForGoalId
   * @param payload {UpdateAssetAllocationForGoalIdOptions} {accountId, async}
   */
  static async updateAssetAllocationForGoalId(payload: UpdateAssetAllocationForGoalIdOptions): Promise<unknown> {
    const sdk = await this.sdkService(defaultOptions);

    return sdk.updateAssetAllocationForGoalId(payload);
  }
}
